/**
 * Matching de los datos leídos del DNI (MRZ del dorso / OCR del anverso)
 * contra los datos cargados del autor. Regla dura: nombre completo, número y
 * fecha de nacimiento legibles Y coincidentes, o el envío no se permite.
 *
 * ESPEJO byte-idéntico en DOS repos (cambios en uno = cambios en el otro, con
 * los mismos vectores de test en ambas suites):
 *   jupiter-server/src/common/dniUpload/dniMatch.ts             (gate server)
 *   jupiter-client/src/common/pages/dniUpload/dniMatch.ts                    (browser)
 */

import { isMrzLegible, type MrzTd1Data } from "./mrzTd1";

export interface AuthorRef {
  /** DNI esperado (dígitos; puede traer ceros a la izquierda si vino de CUIT). */
  dni: string;
  lastName: string;
  firstName: string;
  /** ISO string o Date; se compara solo la parte fecha. */
  dateOfBirth: string | Date;
}

export type DniRejectReason =
  | "mrz_ilegible"
  | "mrz_no_valida"
  | "dni_mismatch"
  | "birth_mismatch"
  | "name_mismatch"
  | "anverso_dni"
  | "anverso_numero_ajeno"
  | "anverso_otro_documento"
  | "anverso_sin_referencia"
  | "anverso_apellido";

/** Upper ASCII sin diacríticos, solo [A-Z ] colapsado — como translitera el MRZ. */
export function normalizeName(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * El DNI esperado a partir del taxId del autor: en un CUIT/CUIL de 11 dígitos
 * el DNI son los dígitos 3..10 (conservando ceros a la izquierda de DNIs de 7);
 * para DU u otro ID, los dígitos tal cual. Null si no hay dígitos.
 */
export function dniFromTaxId(taxId: string, taxIdType: string): string | null {
  const digits = String(taxId ?? "").replace(/\D+/g, "");
  if (!digits) return null;
  if (taxIdType === "cuitCuil" && digits.length === 11) return digits.slice(2, 10);
  return digits;
}

/** Igualdad de DNI ignorando separadores y ceros a la izquierda. */
export function dniEquals(a: string, b: string): boolean {
  const clean = (s: string) => s.replace(/\D+/g, "").replace(/^0+/, "");
  const ca = clean(a);
  return ca.length > 0 && ca === clean(b);
}

/** Compara la parte fecha sin pasar strings por Date (evita corrimientos de zona). */
export function birthDateMatches(mrzIso: string, dateOfBirth: string | Date): boolean {
  const dbDate =
    dateOfBirth instanceof Date
      ? dateOfBirth.toISOString().slice(0, 10)
      : String(dateOfBirth).slice(0, 10);
  return mrzIso === dbDate;
}

/**
 * Nombre completo contra la línea 3 del MRZ, consciente de que los 30
 * caracteres pueden truncar: el apellido del MRZ debe igualar al de la base
 * (o ser su prefijo si el truncado se lo comió), y de los nombres alcanza con
 * que el primero esté, o que lo leído sea prefijo del nombre completo cargado.
 */
export function nameMatchesMrz(
  lastNameDb: string,
  firstNameDb: string,
  mrz: MrzTd1Data
): boolean {
  const dbSurname = normalizeName(lastNameDb);
  const dbGiven = normalizeName(firstNameDb);
  const mrzSurname = normalizeName(mrz.surname);
  const mrzGiven = normalizeName(mrz.givenNames);

  const exacto =
    mrzSurname.length >= 3 &&
    (mrzSurname === dbSurname || dbSurname.startsWith(mrzSurname)) &&
    mrzGiven.length > 0 &&
    (mrzGiven.split(" ").includes(dbGiven.split(" ")[0]) || dbGiven.startsWith(mrzGiven));
  if (exacto) return true;

  // Lectura real de OCR: el '<' que separa apellido de nombres es justo lo que
  // tesseract confunde con L o K, así que partir la línea es poco confiable —
  // pero las LETRAS de los nombres se leen bien. Se compara contra la línea
  // entera sin separadores, exigiendo apellido y primer nombre completos (y
  // largo mínimo, para que un apellido de dos letras no matchee cualquier cosa).
  const linea = (mrz.nameLineRaw ?? "").replace(/[^A-Z]/g, "");
  if (linea.length < 6) return false;
  const apellido = dbSurname.replace(/ /g, "");
  const primerNombre = dbGiven.split(" ")[0];
  if (apellido.length < 3 || primerNombre.length < 3) return false;
  return linea.includes(apellido) && linea.includes(primerNombre);
}

/** Anverso (a): los dígitos del DNI aparecen en la secuencia de dígitos del texto. */
/**
 * Números que parecen un documento en el texto del frente.
 *
 * Sirve para separar dos cosas que no son lo mismo y hasta ahora se decían
 * igual: que el número NO SE LEA, y que se lea uno que no es el de la persona.
 * El segundo caso pasa siempre al probar con un espécimen, y también cuando
 * alguien sube el documento equivocado; decirle "no se lee" es mentirle y
 * mandarlo a sacar otra foto que va a fallar por lo mismo.
 *
 * Se busca la forma impresa argentina —99.999.999— y las corridas de 7 u 8
 * dígitos que no estén pegadas a otros dígitos. Las fechas quedan afuera por
 * su propia forma: 10.12.1983 no es 1-2 dígitos, punto, tres, punto, tres.
 */
export function numerosDeDocumentoEn(ocrText: string): string[] {
  const encontrados = new Set<string>();
  const conPuntos = ocrText.match(/\b\d{1,2}\.\d{3}\.\d{3}\b/g) ?? [];
  for (const n of conPuntos) encontrados.add(n.replace(/\D/g, "").replace(/^0+/, ""));
  const sueltos = ocrText.match(/(?<!\d)\d{7,8}(?!\d)/g) ?? [];
  for (const n of sueltos) encontrados.add(n.replace(/^0+/, ""));
  return [...encontrados].filter((n) => n.length >= 7);
}

export function digitsContainDni(ocrText: string, dni: string): boolean {
  const wanted = dni.replace(/\D+/g, "").replace(/^0+/, "");
  if (!wanted) return false;
  return ocrText.replace(/\D+/g, "").includes(wanted);
}

function levenshtein(a: string, b: string): number {
  const prev = new Array(b.length + 1).fill(0).map((_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let diagonal = prev[0];
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = prev[j];
      prev[j] = Math.min(
        prev[j] + 1,
        prev[j - 1] + 1,
        diagonal + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
      diagonal = tmp;
    }
  }
  return prev[b.length];
}

/** Anverso (b): apellido por substring, o algún token con Levenshtein ≤ max(1, len/4). */
export function surnameFuzzyInText(lastNameDb: string, ocrText: string): boolean {
  const surname = normalizeName(lastNameDb);
  if (!surname) return false;
  const text = normalizeName(ocrText);
  if (text.includes(surname)) return true;
  const tolerance = Math.max(1, Math.floor(surname.length / 4));
  return text
    .split(" ")
    .some((token) => Math.abs(token.length - surname.length) <= tolerance &&
      levenshtein(token, surname) <= tolerance);
}

/**
 * Regla dura del dorso: checksums MRZ + los 3 datos coincidentes, con causa.
 *
 * `faltantesNoContradicen` es para cuando lo que se compara puede estar
 * INCOMPLETO: el registro de la base que la página de validación viene
 * justamente a completar. Un dato que no está no rechaza la foto —no hay nada
 * que contradecir—, pero uno presente y distinto sí, que es el caso que
 * importa: ahí la foto es de otro documento. La tolerancia es sobre lo
 * comparado, nunca sobre la lectura: un MRZ ilegible se rechaza igual.
 */
export function validateDorso(
  mrz: MrzTd1Data | null,
  a: AuthorRef,
  opciones: { faltantesNoContradicen?: boolean } = {}
): { ok: boolean; reason?: DniRejectReason } {
  // La regla de legibilidad vive en UN solo lugar (isMrzLegible). Estaba
  // duplicada acá y se desincronizó: el parser ya aceptaba una lectura sin el
  // dígito compuesto y esta copia la seguía rechazando.
  if (!mrz) {
    return { ok: false, reason: "mrz_ilegible" };
  }
  if (!isMrzLegible(mrz)) {
    // Otra cosa que "no pudimos leer": la banda SE LEYÓ y sus propios dígitos
    // de control no cierran. Sacar otra foto no lo arregla, y decir que la foto
    // está mal manda a intentarlo de nuevo para nada. Pasa con los especímenes
    // publicados, cuyo MRZ está inventado, y pasaría con un documento adulterado.
    return { ok: false, reason: "mrz_no_valida" };
  }
  const falta = (valor: unknown) =>
    opciones.faltantesNoContradicen === true && String(valor ?? "").trim() === "";

  if (!falta(a.dni) && !dniEquals(mrz.documentNumber, a.dni)) {
    return { ok: false, reason: "dni_mismatch" };
  }
  if (!falta(a.dateOfBirth) && !birthDateMatches(mrz.birthDate, a.dateOfBirth)) {
    return { ok: false, reason: "birth_mismatch" };
  }
  if (!falta(a.lastName) && !nameMatchesMrz(a.lastName, a.firstName, mrz)) {
    return { ok: false, reason: "name_mismatch" };
  }
  return { ok: true };
}

/**
 * Criterio del anverso: que aparezca el número de documento.
 *
 * Es más laxo que el dorso a propósito. El frente no tiene dígitos
 * verificadores, y su texto está impreso sobre el fondo de seguridad: medido
 * sobre fotos reales, el apellido se pierde con solo recomprimir la imagen,
 * mientras el número aguanta. Exigir el apellido acá rechazaba fotos buenas
 * por el dato que peor se lee —y que YA viene verificado del dorso, por MRZ y
 * con dígitos verificadores, que es prueba más fuerte que este OCR—.
 *
 * Lo que el número garantiza es que las DOS FOTOS SON DEL MISMO DOCUMENTO, y
 * por eso la referencia preferida es el MRZ del dorso y no los datos cargados:
 *
 *  - Es más fuerte: el número del MRZ viene con dígitos de control, el del
 *    formulario lo tecleó alguien.
 *  - Está antes: el formulario puede estar vacío justamente porque se va a
 *    completar desde el documento, y ahí no habría contra qué mirar el frente.
 *
 * Sin dorso, los datos cargados siguen sirviendo de referencia. Sin ninguna de
 * las dos no se puede afirmar nada, y eso se dice con su propia causa: el
 * número del frente puede estar perfecto y aun así no haber con qué compararlo.
 */
export function validateAnverso(
  ocrText: string,
  a: AuthorRef,
  mrzDelDorso?: MrzTd1Data | null
): { ok: boolean; reason?: DniRejectReason; leido?: string } {
  const delDorso = String(mrzDelDorso?.documentNumber ?? "").trim();
  const referencia = delDorso || a.dni;
  if (!String(referencia ?? "").trim()) {
    return { ok: false, reason: "anverso_sin_referencia" };
  }
  if (!digitsContainDni(ocrText, referencia)) {
    // La diferencia importa: si hay un número legible pero es otro, el problema
    // no es la foto y sacar otra no lo arregla.
    const ajenos = numerosDeDocumentoEn(ocrText);
    if (delDorso) {
      // Decir "no son el mismo documento" sin haber leído NINGÚN número en el
      // frente es una acusación sin prueba: lo único que pasó es que no se pudo
      // confirmar. Medido sobre una foto con las dos caras de un mismo DNI, el
      // frente no entregó su número y se avisaba que eran documentos distintos.
      if (ajenos.length > 0) return { ok: false, reason: "anverso_otro_documento", leido: ajenos[0] };
      return { ok: false, reason: "anverso_dni" };
    }
    if (ajenos.length > 0) {
      return { ok: false, reason: "anverso_numero_ajeno", leido: ajenos[0] };
    }
    return { ok: false, reason: "anverso_dni" };
  }
  return { ok: true };
}

/** Una palabra del OCR con su caja, como la devuelve tesseract. */
export interface PalabraOcr {
  text: string;
  bbox: { x0: number; y0: number; x1: number; y1: number };
}

/**
 * El sexo tal como lo imprime el FRENTE, buscado por GEOMETRÍA.
 *
 * Sirve como segunda lectura de un campo que el MRZ no puede verificar: el
 * dígito compuesto del TD1 no cubre la posición del sexo, así que la lectura
 * del dorso está sola.
 *
 * Se busca debajo del rótulo y no cerca de él en el texto, y esa distinción es
 * la que hace que funcione. El frente es una GRILLA: una fila de rótulos y
 * debajo la de valores. En orden de lectura, entre "Sexo" y su "M" se cruzan
 * las palabras de las columnas vecinas —medido sobre una foto real, cinco—,
 * así que cualquier criterio de cercanía textual falla. A 16 píxeles por
 * debajo, en cambio, no hay nada más.
 *
 * Nunca se toma una letra suelta sin rótulo arriba: una "M" aparece en
 * cualquier lado del documento y tomarla sería inventar.
 */
export function sexoEnElFrente(palabras: PalabraOcr[]): "M" | "F" | null {
  const limpio = (t: string) =>
    String(t ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase()
      .replace(/[^A-Z]/g, "");

  const rotulos = (palabras ?? []).filter((w) => /^SEX/.test(limpio(w.text)));
  for (const rotulo of rotulos) {
    const alto = Math.max(1, rotulo.bbox.y1 - rotulo.bbox.y0);
    const candidatos = (palabras ?? []).filter((w) => {
      const t = limpio(w.text);
      if (t !== "M" && t !== "F") return false;
      // Debajo del rótulo, a no más de tres renglones: más abajo ya es otro campo.
      if (w.bbox.y0 < rotulo.bbox.y1 - alto / 2) return false;
      if (w.bbox.y0 > rotulo.bbox.y1 + alto * 3) return false;
      // Y en la MISMA columna: sin esto, el valor de al lado serviría igual.
      return w.bbox.x0 <= rotulo.bbox.x1 && w.bbox.x1 >= rotulo.bbox.x0;
    });
    if (candidatos.length === 1) return limpio(candidatos[0].text) as "M" | "F";
  }
  return null;
}

/**
 * ¿El frente nombra al país que el MRZ codifica?
 *
 * El MRZ lo trae en tres letras (ARG) y el frente lo imprime entero
 * (ARGENTINA): se confirma cuando alguna palabra del frente EMPIEZA con el
 * código. Alcanza para los casos comunes de un DNI argentino y no inventa
 * ninguno: si no coincide, no se afirma nada —no es una contradicción, es que
 * no se pudo confirmar—. Hay códigos que no son prefijo de su país en español
 * (PRY / PARAGUAY) y ahí simplemente no confirma.
 */
export function nacionalidadEnElFrente(ocrText: string, codigoMrz: string | null): boolean {
  const codigo = String(codigoMrz ?? "").toUpperCase().replace(/[^A-Z]/g, "");
  if (codigo.length !== 3) return false;
  return String(ocrText ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .split(/[^A-Z]+/)
    .some((palabra) => palabra.length >= 4 && palabra.startsWith(codigo));
}
